import axios from "axios";

import { postLogout } from "../../actions";
import { closeWebSocket } from "./websocketclient";
import { jsError } from "../../common/utils";
import { queryClient, store } from "../../main";
import { getApiUrl } from "./getApiUrl.ts";
import { initSettings } from "../../common/settings.js";

const API_BASE_URL = getApiUrl();
const loginPath = "/login/hr";
const verifyMfaAndLoginPath = "/login/hr/verify";

export let access_token = null;
export let refresh_token = null;
let refreshTimeout;

export const getFile = (url) =>
  fetch(API_BASE_URL + url, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });

// web-dispatcher
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  maxRedirects: 0,
  headers: {
    "Content-Type": "application/json",
  },
});

const refresh = async () => {
  try {
    await postRefresh();
  } catch {
    store.dispatch(postLogout());
  }
};

// use refresh_token to get a new access_token 60 seconds before access_token expiration
function setRefreshTimeout(expires_in) {
  clearTimeout(refreshTimeout);
  const seconds = expires_in - 60;
  const timeout = seconds < 5 ? 5 : seconds;
  refreshTimeout = setTimeout(refresh, timeout * 1000);
}

export function logout() {
  access_token = null;
  refresh_token = null;
  clearTimeout(refreshTimeout);
  closeWebSocket();
  sessionStorage.removeItem("esalary-refresh");
  queryClient.clear();
  initSettings(); // clear cached dimensions and topPath
  //navigate("/");
  // TODO: logout from the authentication server (revoke tokens)
}

export async function postLogin(username, password, code) {
  queryClient.clear(); // make sure that no cached data from previous user is visible before login completes

  const body = { username, password };
  if (code) body.code = code;

  const resp = await apiClient.post(code ? verifyMfaAndLoginPath : loginPath, body);

  if (resp.data.access_token) {
    setRefreshTimeout(resp.data.expires_in);
    access_token = resp.data.access_token;
    refresh_token = resp.data.refresh_token;
    sessionStorage.setItem("esalary-refresh", refresh_token);
  }

  // remove company-related cached statistics-data at login
  localStorage.removeItem("workhours");
  localStorage.removeItem("pekkaset");

  return resp;
}

export async function postRefresh(refreshToken = refresh_token) {
  const resp = await apiClient.post(loginPath, { refresh_token: refreshToken });
  setRefreshTimeout(resp.data.expires_in);

  access_token = resp.data.access_token;
  refresh_token = resp.data.refresh_token;
  sessionStorage.setItem("esalary-refresh", refresh_token);

  return resp;
}

export function serverGetPublic(url) {
  return apiClient.get(url).catch((e) => {
    jsError("serverGetPublic catch", e.response);
    throw e;
  });
}

export function serverPostPublic(url, params = null, contentType = "application/json") {
  return apiClient
    .post(url, params, {
      headers: {
        "Content-Type": contentType,
      },
    })
    .catch((e) => {
      jsError("serverPostPublic catch", e.response);
      throw e;
    });
}

export function serverGet(url, params, extraHeaders) {
  return apiClient
    .get(url, {
      params: params,
      headers: {
        ...extraHeaders,
        Authorization: "Bearer " + access_token,
      },
    })
    .catch((e) => {
      jsError("serverGet catch", url, params, extraHeaders, e.response);
      if (e.response && e.response.status === 401) {
        postRefresh().then(() => {
          return apiClient.get(url, {
            headers: {
              Authorization: "Bearer " + access_token,
            },
          });
        });
      } else {
        throw e;
      }
    });
}

export function serverPost(url, params, contentType) {
  const headers = {
    Authorization: "Bearer " + access_token,
  };
  if (contentType) {
    headers["Content-Type"] = contentType;
  }
  return apiClient.post(url, params, { headers }).catch((e) => {
    jsError("serverPost catch", e.response);
    if (e.response && e.response.status === 401) {
      postRefresh().then(() => {
        return apiClient.post(url, params, {
          headers: {
            Authorization: "Bearer " + access_token,
          },
        });
      });
    } else {
      throw e;
    }
  });
}

export function serverPatch(url, params) {
  return apiClient
    .patch(url, params, {
      headers: {
        Authorization: "Bearer " + access_token,
      },
    })
    .catch((e) => {
      jsError("serverPost catch", e.response);
      if (e.response && e.response.status === 401) {
        postRefresh().then(() => {
          return apiClient.patch(url, params, {
            headers: {
              Authorization: "Bearer " + access_token,
            },
          });
        });
      } else {
        throw e;
      }
    });
}

export function serverPut(url, params) {
  return apiClient
    .put(url, params, {
      headers: {
        Authorization: "Bearer " + access_token,
      },
    })
    .catch((e) => {
      jsError("serverPut catch", e.response);
      if (e.response && e.response.status === 401) {
        postRefresh().then(() => {
          return apiClient.patch(url, params, {
            headers: {
              Authorization: "Bearer " + access_token,
            },
          });
        });
      } else {
        throw e;
      }
    });
}

export function serverDelete(url) {
  return apiClient
    .delete(url, {
      headers: {
        Authorization: "Bearer " + access_token,
      },
    })
    .catch((e) => {
      jsError("serverDelete catch", e.response);
      if (e.response && e.response.status === 401) {
        postRefresh().then(() => {
          return apiClient.delete(url, {
            headers: {
              Authorization: "Bearer " + access_token,
            },
          });
        });
      } else {
        throw e;
      }
    });
}
